import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import BusinessMpProvider from "@/core/data-provider/data-mp/business/business-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response";
import { setSafeBalanceInfo } from "@/domains/business/slice";
import toast from "@/domains/toasts/helpers/toast";

export function useSafeHandlers() {
  const { t, i18n } = useTranslation("business");
  const dispatch = useAppDispatch();

  const [isPending, setIsPending] = useState(false);

  const uploadBalance = useCallback(
    async (businessId: number) => {
      try {
        const response = await BusinessMpProvider.getBalance(businessId);

        if (response instanceof RpcMpProviderFailedResponse) {
          if (i18n.exists(`business:business.errors.${response.error.message}`)) {
            console.error(response.error.message);
            toast.error(t(`business.errors.${response.error.message}`));
          } else {
            console.error(response.error.message);
            toast.error(t("business.toast.safeBalanceError"));
          }
          return;
        }

        dispatch(setSafeBalanceInfo(response.data));
      } catch (e) {
        console.error(e);
        toast.error(t("business.toast.safeBalanceError"));
      }
    },
    [dispatch, i18n, t],
  );

  const takeFromSafe = useCallback(
    async (businessId: number, amount: number) => {
      setIsPending(true);
      try {
        const response = await BusinessMpProvider.takeFromSafe(businessId, amount);

        if (response instanceof RpcMpProviderFailedResponse) {
          if (i18n.exists(`business:business.errors.${response.error.message}`)) {
            console.error(response.error.message);
            toast.error(t(`business.errors.${response.error.message}`));
          } else {
            console.error(response.error.message);
            toast.error(t("business.toast.safeMoneyNotTransferred"));
          }
          return;
        }

        await uploadBalance(businessId);
        toast.success(t("business.toast.safeMoneyTransferred"));
      } catch (e) {
        console.error(e);
        toast.error(t("business.toast.safeMoneyNotTransferred"));
      } finally {
        setIsPending(false);
      }
    },
    [i18n, t, uploadBalance],
  );

  return {
    isPending,
    takeFromSafe,
  };
}
